import { Folder } from '@/api/generatedApi';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { useGetFolder } from '@/hooks/api/folders/useGetFolder';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useAssetUploadContext } from '@/services/assetUpload/useAssetUploadContext';
import { useTenants } from '@/services/tenants/useTenants';
import { useQueryClient } from '@tanstack/react-query';
import { FilterAlt, FilterList, ViewColumn } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/EditOutlined';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { Box, Divider, IconButton, Tooltip, useTheme } from '@mui/material';
import {
  GridPreferencePanelsValue,
  GridSortModel,
  GridToolbarProps,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { ReactNode } from 'react';
import { ViewMode } from '@/hooks/useViewMode';
import { useSimpleModal } from '@/hooks/useModal';
import { CreateContentButton } from '../AssetsPage/CreateContentButton';
import { FolderBreadcrumbs } from '../AssetsPage/FolderBreadcrumbs';
import { DroppableBreadcrumbs } from '../AssetsPage/DroppableBreadcrumbs';
import { useDndContext } from '@dnd-kit/core';
import { RenameFolderModal } from '../modals/RenameFolderModal';
import { MoveFolderModal } from '../modals/MoveFolderModal/MoveFolderModal';
import { BulkActionsToolbar } from './BulkActionsToolbar';
import { RefreshContents } from './RefreshContents';
import { SortButton } from './SortButton';
import { ToolbarDescription } from './ToolbarDescription';

const ColumnsButton = () => {
  const apiReference = useGridApiContext();
  const theme = useTheme();

  const handleClick = () => {
    apiReference.current.showPreferences(GridPreferencePanelsValue.columns);
  };

  return (
    <Tooltip title='Columns' placement='bottom'>
      <IconButton
        size='small'
        onClick={handleClick}
        sx={{
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <ViewColumn fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const FiltersButton = () => {
  const apiReference = useGridApiContext();
  const theme = useTheme();
  const filterModel = useGridSelector(apiReference, gridFilterModelSelector);
  const hasActiveFilters = filterModel.items.some(
    item => item.value !== undefined && item.value !== '' && (Array.isArray(item.value) ? item.value.length > 0 : true)
  );

  const handleClick = () => {
    apiReference.current.showFilterPanel();
  };

  const FilterIcon = hasActiveFilters ? FilterAlt : FilterList;

  return (
    <Tooltip title='Filter' placement='bottom'>
      <IconButton
        size='small'
        onClick={handleClick}
        sx={{
          color: hasActiveFilters ? theme.palette.primary.main : theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <FilterIcon fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const RenameFolderButton = ({ folder, onRenameClick }: { folder?: Folder; onRenameClick: () => void }) => {
  const theme = useTheme();

  return (
    <Tooltip title='Rename folder' placement='bottom'>
      <IconButton
        size='small'
        onClick={onRenameClick}
        disabled={!folder}
        sx={{
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <EditIcon sx={{ fontSize: '18px' }} />
      </IconButton>
    </Tooltip>
  );
};

const MoveFolderButton = ({ folder, onMoveClick }: { folder?: Folder; onMoveClick: () => void }) => {
  const theme = useTheme();

  return (
    <Tooltip title='Move folder' placement='bottom'>
      <IconButton
        size='small'
        onClick={onMoveClick}
        disabled={!folder}
        sx={{
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <DriveFileMoveIcon sx={{ fontSize: '18px' }} />
      </IconButton>
    </Tooltip>
  );
};

export const ContentsTableToolbar = ({
  folderId,
  showRootFolder,
  bulkActions,
  showCreateButton = true,
  showFiltersButton = true,
  onRefresh,
  toolbarActions,
  viewMode,
  onSortModelChange,
}: GridToolbarProps & {
  folderId?: string;
  showRootFolder?: boolean;
  bulkActions?: ReactNode;
  showCreateButton?: boolean;
  showFiltersButton?: boolean;
  onRefresh?: () => void;
  toolbarActions?: ReactNode;
  viewMode?: ViewMode;
  onSortModelChange?: (model: GridSortModel) => void;
}) => {
  const { folder: parentFolder } = useGetFolder({ folderId });
  const { getTenantId } = useTenants();
  const { permissions } = useGetCurrentUser();
  const { handleFileUpload, handleFolderUpload } = useAssetUploadContext();
  const queryClient = useQueryClient();
  const renameModal = useSimpleModal();
  const moveModal = useSimpleModal();
  const { active: dndActive } = useDndContext();

  const canUploadAndMakeFolders =
    showCreateButton && [permissions.canUploadAssets, permissions.canCreateFolders].some(Boolean);
  const isDragging = !!dndActive;
  const showFolderActions = folderId && permissions.canCreateFolders;

  const handleRenameClick = () => {
    renameModal.open();
  };

  const handleMoveClick = () => {
    moveModal.open();
  };

  const handleRefreshContents = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      queryClient.resetQueries({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false });
    }
  };

  if (!getTenantId()) {
    return;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          padding: '8px 10px 6px 8px',
          flexWrap: 'nowrap',
          gap: '6px',
          minWidth: 0,
          overflow: 'visible',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            marginLeft: '8px',
          }}
        >
          {isDragging ? (
            <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1, display: 'flex', alignItems: 'center' }}>
              <DroppableBreadcrumbs folderId={folderId} />
            </Box>
          ) : (
            <>
              <Box sx={{ minWidth: 0, overflow: 'hidden', flex: '0 1 auto' }}>
                <FolderBreadcrumbs folderId={folderId} showRootFolder={showRootFolder} />
              </Box>
              {canUploadAndMakeFolders && (
                <Box
                  sx={{
                    overflow: 'visible',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CreateContentButton
                    parentFolder={parentFolder}
                    handleFileUpload={handleFileUpload}
                    handleFolderUpload={handleFolderUpload}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexShrink: 0,
          }}
        >
          {showFolderActions && (
            <>
              <RenameFolderButton folder={parentFolder} onRenameClick={handleRenameClick} />
              <MoveFolderButton folder={parentFolder} onMoveClick={handleMoveClick} />
              <Divider orientation='vertical' flexItem sx={{ mx: 0.5, height: 16, alignSelf: 'center' }} />
            </>
          )}
          {viewMode === 'card' && onSortModelChange ? (
            <SortButton onSortModelChange={onSortModelChange} />
          ) : (
            <ColumnsButton />
          )}
          {showFiltersButton && <FiltersButton />}
          {toolbarActions}
          <RefreshContents onRefresh={handleRefreshContents} />
          <BulkActionsToolbar>{bulkActions}</BulkActionsToolbar>
        </Box>
        <RenameFolderModal isOpen={renameModal.isOpen} onClose={renameModal.close} folder={parentFolder} />
        <MoveFolderModal isOpen={moveModal.isOpen} onClose={moveModal.close} folder={parentFolder} />
      </Box>
      {parentFolder && <ToolbarDescription folder={parentFolder} canEdit={!!permissions.canCreateFolders} />}
    </Box>
  );
};
